/**
 * Contrôle de pagination des listes.
 *
 * Un seul bouton « charger la suite », et pas de numéros de page : l'API pagine
 * par keyset (docs/08 §4) et ne rend qu'un curseur **vers l'avant**. Des numéros
 * promettraient un accès direct au rang 7 que le serveur ne sait pas servir, et
 * un bouton « précédent » exigerait de mémoriser la pile des curseurs pour
 * simuler ce que le protocole ne fait pas.
 *
 * Le nombre de lignes chargées est affiché même quand tout tient sur une page :
 * une liste tronquée en silence est le défaut que cette pagination invisible
 * laissait passer — vingt comptes s'affichaient, et rien ne disait qu'il y en
 * avait trois cents.
 *
 * Promu du back-office vers le design system en phase 3, quand le catalogue est
 * devenu son second usage : `HANDOFF` posait la condition — « à reprendre s'il
 * sort de `features/admin/` ». Ses libellés sont passés du namespace `admin` à
 * `common` par la même occasion, sans quoi une liste du catalogue afficherait
 * des clés d'administration.
 */
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/Button";

interface LoadMoreProps {
  shown: number;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}

export function LoadMore({ shown, hasMore, isLoading, onLoadMore }: LoadMoreProps) {
  const { t } = useTranslation("common");

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {/* `count` et non `shown` : c'est le nom qu'i18next attend pour choisir
            entre les clés `_one` et `_other`. Le passer sous un autre nom
            afficherait toujours le pluriel, y compris pour une seule ligne. */}
        {t("pagination.shown", { count: shown })}
        {!hasMore && ` · ${t("pagination.allShown")}`}
      </p>
      {hasMore && (
        <Button variant="secondary" disabled={isLoading} onClick={onLoadMore}>
          {isLoading ? t("pagination.loading") : t("pagination.loadMore")}
        </Button>
      )}
    </div>
  );
}
